import React from 'react';
import { DATA_RECOVERY } from '@/constants/testIds';
import {
  flagClass,
  priorityBadgeClass,
  priorityAccentClass,
  formatAge,
  formatRelative,
} from './styles';

/**
 * Large TV card. Read-only.
 * Shows only the fields requested by the user:
 *   ticket #, customer name, device/media type, RepairDesk status, board phase,
 *   assigned tech, next action, age in current phase, last customer contact,
 *   priority badge, blocked/warning badges.
 */
export default function TicketCard({ ticket, problem = false }) {
  const flags = ticket.warning_flags || [];
  const statusAge = ticket.status_age_hours ?? ticket.age_in_phase_hours;
  const ticketAge = ticket.ticket_age_hours;
  const isOld = statusAge != null && statusAge >= 24;
  const isAncient = statusAge != null && statusAge >= 72;

  return (
    <div
      data-testid={problem ? DATA_RECOVERY.problemCard : DATA_RECOVERY.card}
      data-ticket-id={ticket.ticket_id}
      className={`bg-slate-900/85 ${priorityAccentClass(ticket.priority)}
        border border-slate-800 rounded-lg shadow-md px-4 py-3
        ${problem ? 'border-rose-700/70' : ''}`}
    >
      {/* Row 1: ticket # + priority */}
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-base lg:text-lg text-slate-300">
          #{ticket.number || ticket.ticket_id}
        </span>
        <span
          className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-bold border ${priorityBadgeClass(
            ticket.priority,
          )}`}
        >
          {ticket.priority}
        </span>
      </div>

      {/* Row 2: customer */}
      <div className="mt-1 text-lg lg:text-xl font-semibold text-slate-50 leading-tight truncate">
        {ticket.customer_name || '—'}
      </div>

      {/* Row 3: device + status */}
      <div className="mt-1 flex items-center gap-2 flex-wrap text-sm">
        {ticket.device_type && (
          <span className="text-slate-200 font-medium">{ticket.device_type}</span>
        )}
        {ticket.device_type && ticket.status_name && (
          <span className="text-slate-600">·</span>
        )}
        {ticket.status_name && (
          <span className="text-slate-400">
            RD: <span className="text-slate-200">{ticket.status_name}</span>
          </span>
        )}
      </div>

      {/* Row 4: next action */}
      {ticket.next_action && (
        <div className="mt-2 text-[13px] lg:text-sm text-slate-100 bg-slate-800/70 border border-slate-700/70 rounded px-2 py-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
            Next:
          </span>
          {ticket.next_action}
          {ticket.next_action_overridden && (
            <span className="ml-1 text-cyan-300" title="Overridden by ops">*</span>
          )}
        </div>
      )}

      {/* Row 5: ages — Open Xd · Status Yd */}
      <div className="mt-2 flex items-center justify-between text-[12px] text-slate-300 font-mono">
        <span className="text-slate-500 uppercase tracking-wider text-[10px]">
          Age
        </span>
        <span>
          <span className="text-slate-500">Open</span>{' '}
          <span className="text-slate-100">{formatAge(ticketAge)}</span>
          <span className="mx-1.5 text-slate-600">·</span>
          <span className="text-slate-500">Status</span>{' '}
          <span
            className={
              isAncient ? 'text-rose-300 font-bold'
              : isOld ? 'text-amber-300'
              : 'text-slate-100'
            }
          >
            {formatAge(statusAge)}
          </span>
        </span>
      </div>

      {/* Row 6: tech + customer contact */}
      <div className="mt-1 flex items-center justify-between text-[12px] text-slate-300">
        <span>
          <span className="text-slate-500">Tech:</span>{' '}
          <span className="text-slate-100 font-medium">{ticket.assigned_tech || '—'}</span>
        </span>
        <span>
          <span className="text-slate-500">Cust:</span>{' '}
          <span className="text-slate-200">{formatRelative(ticket.last_customer_contact_at)}</span>
        </span>
      </div>

      {/* Row 7: warning flags */}
      {flags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {flags.map((f) => (
            <span
              key={f}
              className={`px-2 py-0.5 text-[11px] font-medium rounded border ${flagClass(f)}`}
            >
              {f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
